import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const useWebPush = () => {
  const { user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      setPermission(Notification.permission);
      
      // Check if already subscribed
      navigator.serviceWorker.getRegistration().then(registration => {
        if (registration) {
          registration.pushManager.getSubscription().then(subscription => {
            setIsSubscribed(!!subscription);
          });
        }
      });
    }
  }, []);

  const requestPermissionAndSubscribe = async () => {
    if (!isSupported || !user) return false;
    
    setLoading(true);
    try {
      const currentPermission = await Notification.requestPermission();
      setPermission(currentPermission);
      
      if (currentPermission !== 'granted') {
        toast.error('Permissão para notificações negada.');
        return false;
      }

      // Register Service Worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      
      // Get Subscription
      let subscription = await registration.pushManager.getSubscription();
      
      if (!subscription) {
        // Busca a VAPID public key da edge function (evita depender de VITE_*
        // que pode não estar presente no build de produção).
        let publicVapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
        if (!publicVapidKey) {
          const { data, error } = await supabase.functions.invoke('get-vapid-public-key');
          if (error || !data?.publicKey) {
            console.error('Falha ao obter VAPID public key:', error);
            toast.error('Erro de configuração de push notifications.');
            return false;
          }
          publicVapidKey = data.publicKey as string;
        }

        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicVapidKey)
        });
      }


      // Save to Supabase
      const subscriptionJSON = subscription.toJSON();
      
      if (subscriptionJSON.endpoint && subscriptionJSON.keys) {
        const { error } = await (supabase as any)
          .from('web_push_subscriptions')
          .upsert({
            user_id: user.id,
            endpoint: subscriptionJSON.endpoint,
            p256dh: subscriptionJSON.keys.p256dh,
            auth: subscriptionJSON.keys.auth,
            updated_at: new Date().toISOString()
          }, { onConflict: 'user_id, endpoint' });
          
        if (error) {
          console.error('Error saving push subscription:', error);
          // If we fail to save, we might want to throw or just log
        }
      }

      setIsSubscribed(true);
      toast.success('Notificações ativadas com sucesso!');
      return true;
    } catch (error) {
      console.error('Error subscribing to web push:', error);
      toast.error('Ocorreu um erro ao ativar notificações.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    isSupported,
    permission,
    isSubscribed,
    /** @deprecated use `isLoading` (Lumina Core contract) */
    loading,
    isLoading: loading,
    requestPermissionAndSubscribe
  };
};
